// HomeV2 — batch 2's rebuild of Dashboard (UI rebuild, 26 Oct 2026; drawn when lib/ui-v2.ts switches batch 2 on).
//
// ASTRA'S HOME (the operator's ruling, 26 Oct 2026: "simplified or closer to that version"). One job: see work that needs
// a person and open it directly. Nothing else is on the page:
//   1. PageHead — Home, Dubai's day; Record money and Book trade, the one gold primary.
//   2. Needs attention — one row per kind of work, each with its one button (components/v2/home/AttentionQueue).
//   3. Three shortcuts: Settle later, Wallets, Period close.
//   4. Recent activity — the newest twelve transactions (five on the phone), a trade opening its deal, "Money movements →"
//      and "All trades →" (components/v2/home/RecentActivity).
// RESTORED 1 Nov 2026 (his "restore most the lost things if not all" — "but with the improved UI and not the cluttered as
// the previous one"): the day's four figures — trades today, volume, margin, last trade — the kit's SummaryStrip on the
// laptop, one quiet line on the phone; the Wallets glance (the five accounts that moved, "More accounts (N) →") under the
// shortcuts on the laptop; the queue's rows per settle-later party and per chain transfer. Move money and Convert are on
// New entry.
//
// THE READS ARE DASHBOARD'S, UNDER THE SAME KEYS (screens/Dashboard.tsx holds the history of each; the short why is here):
// open cheques, the settle sweep, the chain, the journal's two reads with their merge and states, the summary and the
// treasury read; "books down" said once. Custody's approvals are read only once custody is switched on (CUSTODY_IN_CONSOLE).
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Notice, OfflineNotice, PageHead, Panel, Pending, ReadFailed, StateLine, SummaryStrip } from '../../components/v2/kit';
import { offlineFailure, readState } from '../../lib/read-state';
import { AttentionQueue, CHAIN_GLANCE_ROWS } from '../../components/v2/home/AttentionQueue';
import { RecentActivity } from '../../components/v2/home/RecentActivity';
import { withAbsentDeals } from '../../components/MovementTable';
import { api, custody, type DealRow, type TradeRow } from '../../lib/api';
import { collapseTotals, dateBucket, displayAsset, fmtAmount, fmtDay, fmtTime, fmtWeekday } from '../../lib/format';
import { partitionByRelevance, partyName, railName } from '../../lib/naming';
import { CUSTODY_IN_CONSOLE, routeAt } from '../../lib/routes';
import { isWebContext } from '../../lib/webSession';

/** RECENT ACTIVITY DEPTH asked of both reads (Dashboard's ACTIVITY_ROWS, in both keys so they cannot drift apart). The
 *  panel names three; twelve keeps the merge right when a trade's legs fold into one entry. */
const ACTIVITY_ROWS = 12;

/** A recent deal in the trades log's row shape — what the journal needs to draw a settle-later trade the movements feed
 *  never carried. */
function asTradeRow(d: DealRow): TradeRow {
  return {
    deal_id: d.deal_id,
    direction: d.direction,
    asset_take: d.asset_take,
    asset_give: d.asset_give,
    amount_take: d.amount_take,
    amount_give: d.amount_give,
    rate: d.rate,
    spread_amount: d.spread_amount,
    payment_method: d.payment_method,
    delivery_method: null,
    booked_at: d.booked_at,
    booked_by_admin: d.booked_by_admin,
    party_slug: d.party_slug ?? '',
    party_name: d.party_name ?? '',
    lifecycle: d.lifecycle,
  };
}

/** The lead figure of a per-asset map: AED when there is one, else the largest (Dashboard's primaryEntry). */
function lead(totals: Record<string, number>): [string, number] | undefined {
  const entries = Object.entries(totals);
  return entries.find(([k]) => k === 'AED') ?? [...entries].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))[0];
}
/** The other assets of a per-asset map, as one quiet line, the unit after the figure: "4,100 USDT · 250,000 INR". */
const rest = (totals: Record<string, number>, first?: [string, number]) =>
  Object.entries(totals).filter(([k]) => k !== first?.[0]).map(([k, v]) => `${fmtAmount(v, k)} ${displayAsset(k)}`).join(' · ');

/** THE WALLETS GLANCE (Dashboard's): the accounts a trade touches — cash in every currency and the hot wallets, never a
 *  bank — the five most relevant; "More accounts (N)" counts every account not shown. */
const GLANCE_RAILS = 5;
const MOVING_KINDS = new Set(['cash', 'crypto', 'hot', 'wallet']);

const BOOK = routeAt('/trade');
const RECORD = routeAt('/record');
/** The shortcuts by use (the Books' last 30 days, 1 Nov 2026): Convert 71 and Move 51 after the head's Book trade and Record
 *  money; then Settle later, the daily list. They sit right under the head, above the queue, so Convert is one tap from
 *  opening the console ("convert button I need handy"). Wallets is on the rail and the tab bar; Period close is monthly. */
const SHORTCUTS = [routeAt('/swap'), routeAt('/treasury/rebalance'), routeAt('/settle')];

export function HomeV2() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  // As deep as the journal: settle-later trades move no account and reach it through this read alone.
  const deals = useQuery({
    queryKey: ['deals', 'recent', ACTIVITY_ROWS],
    queryFn: () => api.recentDeals('dubaiotc', ACTIVITY_ROWS),
  });
  const mcs = useQuery({ queryKey: ['mc', 'pending'], queryFn: () => api.pendingMcs() });
  // Settle-later trades still open. Reading this also runs the sweep, so opening Home keeps the list honest (operator,
  // 2 Sept: "need some mechanism to do that often so that it stays visible").
  const settle = useQuery({ queryKey: ['settlements'], queryFn: () => api.settlements(), refetchOnWindowFocus: true, refetchInterval: 30_000 });
  // On-chain transfers the books have no entry for. THE LIMIT IS PART OF THE KEY: Chain reads the same status with no limit
  // under ['chain', tab]; prefix invalidation on ['chain'] still covers both.
  const chainQ = useQuery({
    queryKey: ['chain', 'pending', CHAIN_GLANCE_ROWS],
    queryFn: () => api.chainActivity('pending', CHAIN_GLANCE_ROWS),
  });
  // Custody's approvals, under Custody's own key — never asked while custody is switched off.
  const approvals = useQuery({
    queryKey: ['custody', 'approvals'],
    queryFn: custody.approvals,
    enabled: CUSTODY_IN_CONSOLE,
    refetchInterval: 15_000,
  });
  // The day's figures and the Wallets glance — the reads Dashboard kept under these keys.
  const summary = useQuery({ queryKey: ['summary'], queryFn: api.summary });
  const treasury = useQuery({ queryKey: ['treasury', 'balances'], queryFn: () => api.treasuryBalances() });
  const money = useQuery({
    queryKey: ['movements', 'home', ACTIVITY_ROWS],
    queryFn: () => api.movements({ entity: 'dubaiotc', limit: ACTIVITY_ROWS }),
    refetchOnWindowFocus: true,
  });
  const recentDeals = deals.data?.deals ?? [];
  const dealsById = useMemo(() => new Map(recentDeals.map((d) => [d.deal_id, asTradeRow(d)])), [deals.data]);   // eslint-disable-line react-hooks/exhaustive-deps

  // RECENT ACTIVITY = THE MOVEMENTS FEED PLUS THE DEALS IT DOES NOT CARRY (withAbsentDeals) — but only over a feed that is
  // CURRENT: against a stale one a settled spot trade looks absent and would be drawn as an open position. A stale feed
  // still shows its rows; nothing is folded into it.
  const feedRows = money.data?.rows;
  const feedCurrent = !!feedRows && !money.isError;
  const activityRows = useMemo(
    () => (feedCurrent ? withAbsentDeals(feedRows, recentDeals, ACTIVITY_ROWS) : (feedRows ?? []).slice(0, ACTIVITY_ROWS)),
    [money.data, money.isError, deals.data],   // eslint-disable-line react-hooks/exhaustive-deps
  );

  // RECENT ACTIVITY IS TWO READS, AND A PANEL THAT LOST ONE SAYS SO, in one line: it waits as a whole only when BOTH have
  // not come; each failure is said whatever the other is doing; "Nothing booked yet" needs BOTH to have succeeded.
  const activityAllPending = money.isPending && deals.isPending;
  const activityBlind = !money.data && !deals.data;
  const activityGap =
    money.isError && deals.isError ? 'trades and money'
    : money.isError ? 'money movements'
    : deals.isError ? 'trades'
    : null;
  const readActivityAgain = () => { void money.refetch(); void deals.refetch(); };
  const activityWarning = activityGap && (
    <ReadFailed what={activityBlind ? 'recent activity' : activityGap} error={money.error ?? deals.error} onRetry={readActivityAgain} />
  );
  const activityWaiting = !activityAllPending && (money.isPending || deals.isPending)
    ? <Pending what={`Reading ${money.isPending ? 'money movements' : 'trades'}…`} />
    : null;
  const activityQuiet = activityRows.length === 0 && money.isSuccess && deals.isSuccess;

  if (me.isLoading) return <Pending />;
  // Web-context 401s never reach here (the api client clears the session and redirects to /login) — this is the
  // network/5xx path.
  // Opened with no connection, the profile read is waiting, not refused — no "sign in again" (lib/read-state.ts offlineFailure).
  if (me.isError && offlineFailure(me.error)) return <OfflineNotice what="your operator profile" />;
  if (me.isError) {
    return (
      <StateLine tone="bad">
        {isWebContext() ? 'Couldn’t read your operator profile — refresh, or sign in again.' : 'Open from inside Telegram.'}
      </StateLine>
    );
  }

  // BOOKS DOWN IS ONE FACT, SAID ONCE (21 Oct 2026 audit): three or more failed reads with NOT ONE read holding anything
  // is the books being down (Docker does not start by itself after a reboot) — one amber line with Try again, and no panel
  // that could pass for an empty day. A partial outage keeps what loaded; each failed source speaks for itself.
  const reads = [summary, deals, money, treasury, settle, mcs, chainQ];
  const failedReads = reads.filter((r) => r.isError && r.data === undefined);
  const booksDown = failedReads.length >= 3 && reads.every((r) => r.data === undefined);

  // THE DAY'S FIGURES: a zero is "0" once the summary has answered; a figure not read is "—". A REFRESH THAT FAILED says so
  // under them, with its way out, and names the older read the figures are (Appendix B; Astra, 1 Nov 2026) — they are never
  // passed off as current.
  const today = summary.data?.today;
  const summaryFailed = readState(summary) === 'failed';
  const volume = collapseTotals(today?.volume_per_asset ?? {});
  const margin = collapseTotals(today?.spread_per_asset ?? {});
  const vol = lead(volume), mgn = lead(margin);
  const lastDeal = recentDeals[0];
  const figure = (e: [string, number] | undefined) => (e
    ? <>{fmtAmount(e[1], e[0])} <span className="font-sans text-meta font-normal text-tg-hint">{displayAsset(e[0])}</span></>
    : today ? '0' : '—');
  const strip = [
    { label: 'Trades today', value: today ? String(today.trade_count) : '—' },
    { label: 'Volume', value: figure(vol), sub: rest(volume, vol) || undefined },
    { label: 'Margin', value: figure(mgn), sub: rest(margin, mgn) || undefined },
    // Its Dubai time, as Recent activity says it ("09:15", "25 Oct 16:00"); the amount whole, the party cut at the cell's edge
    // (the whole name is the title, and the deal's).
    { label: 'Last trade', value: lastDeal ? (dateBucket(lastDeal.booked_at) === 'today' ? fmtTime(lastDeal.booked_at) : `${fmtDay(lastDeal.booked_at)} ${fmtTime(lastDeal.booked_at)}`) : '—',
      sub: lastDeal ? (
        <span className="flex min-w-0">
          <span className="shrink-0 whitespace-nowrap">{fmtAmount(lastDeal.amount_take, lastDeal.asset_take)} {displayAsset(lastDeal.asset_take)}&nbsp;·&nbsp;</span>
          <span className="min-w-0 truncate" title={partyName({ display_name: lastDeal.party_name, slug: lastDeal.party_slug })}>
            {partyName({ display_name: lastDeal.party_name, slug: lastDeal.party_slug })}
          </span>
        </span>
      ) : undefined },
  ];
  // THE WALLETS GLANCE.
  const allRails = treasury.data?.rows ?? [];
  const moving = allRails.filter((r) => MOVING_KINDS.has((r.treasuryKind ?? r.slug?.split('.')[0] ?? '').toLowerCase()));
  const glance = partitionByRelevance(moving, { maxTier: 1 }).shown.slice(0, GLANCE_RAILS);
  const moreRails = allRails.length - glance.length;

  return (
    <>
      <PageHead
        title="Home"
        sub={`${fmtWeekday().replace(/ \d{4}$/, '')} · Dubai desk`}   // no current year (HOUSE-STYLE rule 11)
        actions={
          <>
            <Link to={RECORD.path} className="btn-secondary px-4 text-body">{RECORD.title}</Link>
            <Link to={BOOK.path} className="btn-primary px-4 text-body"><Plus className="h-4 w-4" aria-hidden />{BOOK.title}</Link>
          </>
        }
      />

      {!booksDown && (
        <>
          {/* The same four cells on the phone, two a row (his "one fact per place", 1 Nov 2026: it was a sentence there). */}
          <SummaryStrip items={strip} cols={4} phoneCols={2} />
          {summaryFailed && (
            <div className="-mt-2 mb-4 lg:mt-3">
              <ReadFailed what="today's figures" error={summary.error} onRetry={() => { void summary.refetch(); }} tone={today ? 'warn' : 'bad'} />
              {today && summary.dataUpdatedAt > 0 && <p className="mt-1 text-meta text-tg-hint">Shown: the read of {fmtTime(summary.dataUpdatedAt)}</p>}
            </div>
          )}
        </>
      )}

      {booksDown ? (
        <Notice tone="warn">
          <ReadFailed what="the books" error={failedReads[0]?.error} onRetry={() => { reads.forEach((r) => { void r.refetch(); }); }} />
        </Notice>
      ) : (
        // The laptop: the shortcuts and the queue in the wide column, Recent activity the compact one beside them. The
        // phone: one column — the shortcuts right under the two actions, then the queue, then Recent activity, closed.
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:grid-rows-[auto_auto_1fr] lg:gap-x-5 lg:gap-y-6">
          <nav aria-label="Shortcuts" className="grid grid-cols-3 gap-1.5 lg:col-start-1 lg:flex lg:flex-wrap lg:gap-3">
            {SHORTCUTS.map((r) => {
              const Icon = r.icon;
              return (
                <Link key={r.path} to={r.path} className="btn-secondary min-w-0 gap-1 whitespace-nowrap px-1.5 text-meta lg:gap-1.5 lg:px-3 lg:text-body">
                  {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}{r.title}
                </Link>
              );
            })}
          </nav>
          <AttentionQueue
            approvals={CUSTODY_IN_CONSOLE ? approvals : null}
            mcs={mcs}
            settle={settle}
            chain={chainQ}
            role={me.data?.role === 'read_only' ? 'Read only' : 'Full access'}
            className="lg:col-start-1"
          />
          {/* On the phone too (the audit, 1 Nov 2026: the phone lost the facts the laptop shows), under the queue. */}
          <Panel title="Wallets" action={<Link to="/treasury" className="btn-link text-body">All accounts →</Link>} flush className="lg:col-start-1">
            {/* A REFRESH THAT FAILED says so over the last read, with its way out (Appendix B): the balances below are that
                older read, and an empty claim needs a read that worked. */}
            {treasury.isError && (
              <div className="px-5 py-3">
                <ReadFailed what="the accounts" error={treasury.error} onRetry={() => { void treasury.refetch(); }} tone={treasury.data ? 'warn' : 'bad'} />
                {treasury.data && treasury.dataUpdatedAt > 0 && <p className="mt-1 text-meta text-tg-hint">Shown: the read of {fmtTime(treasury.dataUpdatedAt)}</p>}
              </div>
            )}
            {treasury.isPending ? <Pending what="Reading the accounts…" />
              : !treasury.data ? null
              : glance.length === 0 ? (treasury.isError ? null : <p className="px-5 py-4 text-body text-tg-hint">No account moved.</p>)
              : (
                <ul>
                  {glance.map((r) => (
                    <li key={r.account} className="flex items-baseline justify-between gap-4 border-b border-stroke-soft px-5 py-3 last:border-b-0">
                      <span className="min-w-0 truncate text-body">{railName(r)}</span>
                      <span className={`shrink-0 whitespace-nowrap font-mono tabular text-body ${r.balance < 0 ? 'text-neg' : ''}`}>
                        {fmtAmount(r.balance, r.asset)} <span className="font-sans text-meta text-tg-hint">{displayAsset(r.asset)}</span>
                      </span>
                    </li>
                  ))}
                  {moreRails > 0 && (
                    <li className="px-5 py-2"><Link to="/treasury" className="btn-link flex min-h-[40px] items-center text-meta">More accounts ({moreRails}) →</Link></li>
                  )}
                </ul>
              )}
          </Panel>
          <RecentActivity
            className="lg:col-start-2 lg:row-span-3 lg:row-start-1"
            rows={activityRows}
            dealsById={dealsById}
            allPending={activityAllPending}
            quiet={activityQuiet}
            notes={(activityWarning || activityWaiting) ? <>{activityWarning}{activityWaiting}</> : null}
          />
        </div>
      )}
    </>
  );
}
